<script setup lang="ts">
import { onBeforeUnmount, reactive } from 'vue';

import { fetchAttachmentUrl } from '@/api/attachments';
import { formatFileSize } from '@/tools/format';
import type { WidgetAttachment } from '@/api/types';

const props = defineProps<{
  ticketId: string;
  attachments: WidgetAttachment[];
}>();

/** A link per image asked for, or `null` once asking for it has failed. */
const urls = reactive(new Map<string, string | null>());

/** Images a link has been asked for, whatever came of it. */
const requested = new Set<string>();

const isImage = (attachment: WidgetAttachment): boolean => attachment.mimeType.startsWith('image/');

const load = (fileId: string): void => {
  if (requested.has(fileId)) {
    return;
  }

  requested.add(fileId);

  fetchAttachmentUrl(props.ticketId, fileId)
    .then((url) => urls.set(fileId, url))
    .catch(() => urls.set(fileId, null));
};

/**
 * A link is asked for when its image comes into view, not when the thread is
 * drawn.
 *
 * Each link is a request, and the API allows a contact sixty of them a minute.
 * A long thread can hold more images than that, so asking for all of them at
 * once would spend the allowance on thumbnails nobody has scrolled to and leave
 * the ones on screen as failures. A panel shows a few at a time. Only images
 * are fetched ahead at all: a document is asked for when it is clicked.
 */
const observer = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (entry.isIntersecting) {
      observer.unobserve(entry.target);
      load((entry.target as HTMLElement).dataset['fileId'] ?? '');
    }
  }
});

/** Called by Vue with each thumbnail's element, on every render that keeps it. */
const watchForView = (element: unknown): void => {
  if (element instanceof HTMLElement && !requested.has(element.dataset['fileId'] ?? '')) {
    observer.observe(element);
  }
};

onBeforeUnmount(() => {
  observer.disconnect();
});

/**
 * Open the file in a new tab, through a link asked for at the click.
 *
 * Not the address the thumbnail was drawn from: that one is minutes old by the
 * time a thread has sat open, and a link past its time answers nothing. A
 * recent one is reused, so this is usually no request at all.
 */
const open = async (attachment: WidgetAttachment): Promise<void> => {
  try {
    window.open(await fetchAttachmentUrl(props.ticketId, attachment.id), '_blank', 'noopener');
  } catch {
    urls.set(attachment.id, null);
  }
};
</script>

<template>
  <div class="attachments">
    <template
      v-for="attachment in attachments"
      :key="attachment.id"
    >
      <button
        v-if="isImage(attachment) && urls.get(attachment.id) !== null"
        :ref="watchForView"
        type="button"
        class="shot"
        :title="attachment.name"
        :data-file-id="attachment.id"
        data-testid="widget-message-image-open"
        @click="open(attachment)"
      >
        <img
          v-if="urls.get(attachment.id)"
          class="shot__image"
          data-testid="widget-message-image"
          :src="urls.get(attachment.id) ?? undefined"
          :alt="attachment.name"
          @error="urls.set(attachment.id, null)"
        >
      </button>

      <button
        v-else
        type="button"
        class="file"
        data-testid="widget-message-file"
        @click="open(attachment)"
      >
        <span class="file__name">{{ attachment.name }}</span>
        <span class="file__size">{{ formatFileSize(attachment.size) }}</span>
      </button>
    </template>
  </div>
</template>

<style scoped>
.attachments {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: inherit;
  max-width: 100%;
}

/* A fixed box, drawn before the image arrives: the thread is scrolled to its
   end as soon as it is read, and an image that grew into place afterwards
   would push the newest turn back out of view. */
.shot {
  display: block;
  width: 220px;
  max-width: 100%;
  height: 150px;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--w-border);
  border-radius: var(--w-radius);
  background: var(--w-bg-subtle);
}

.shot__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file {
  display: flex;
  gap: 8px;
  align-items: baseline;
  max-width: 100%;
  padding: 6px 10px;
  border: 1px solid var(--w-border);
  border-radius: 8px;
  background: var(--w-bg);
  font-size: 13px;
  text-align: left;
}

.file__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: underline;
}

.file__size {
  flex: none;
  color: var(--w-text-muted);
  font-size: 12px;
}
</style>
